import {useEffect, useState} from 'react'
import {Form, Select} from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import moment from 'moment'
import PrimaryButton from 'src/components/elements/Buttons/PrimaryButton/PrimaryButton'
import SecondaryButton from 'src/components/elements/Buttons/SecondaryButton/SecondaryButton'
import DialogCustom from 'src/components/elements/DialogCustom/DialogCustom'
import apiClient from 'src/services/apiClient'
import {STATUS} from 'src/constants/constants'
import {
  SECONDARY_THERAPY_DEFAULT_DATA_SCHEMA,
  SecondaryTherapyDataSchemaElement,
  secondaryTherapyErrorMessage
} from 'src/constants/secondaryTherapyFields'
import {useAppSelector} from 'src/hooks'
import {Typography} from '@mui/material'
import FrequencyFields from '../TherapyDialogs/FrequencyFields'
import DosageFields from '../TherapyDialogs/DosageFields'
import ModeOfAssumptionFields from '../TherapyDialogs/ModeOfAssumptionFields'
import Stepper from '../TherapyDialogs/Stepper'
import {BoldLabelsFormWrapper, StepperGreyContainer} from '../TherapyDialogs/TherapyGridStyled'
import {
  DEFAULT_CALCULATION_CRITERION_UNIT_TYPE,
  DEFAULT_DOSAGE_UNIT_TYPE,
  DEFAULT_FREQUENCY_UNIT_TYPE,
  DEFAULT_INFUSION_RATE_UNIT_TYPE,
  DEFAULT_SPEED_UNIT_TYPE,
  DEFAULT_TOLERANCE_DAYS_UNIT_TYPE
} from '../TherapyDialogs/therapyDialogsConstants'

type Option = {
  value: string,
  label: string,
}

const buildDosageInitialValue = (unitType: string, dosage?: Record<string, any>) => ({unitType, ...dosage})

const buildCustomDosagesInitialValue = (customDosages?: Record<string, any>[]) => customDosages?.map((customDosage) => ({
  dosage: {value: customDosage.value, unitType: customDosage.unitType},
  infusionRate: customDosage.infusionRate,
  secondaryDosage: customDosage.secondaryDosage,
  dilution: customDosage.dilution,
  administrationHour: customDosage.administrationHour ? moment(customDosage.administrationHour, 'HH:mm') : undefined
}))

type SecondaryTherapyDialogProps = {
  secondaryTherapy?: Record<string, any>;
  onClose: () => void;
  onSuccess: () => void;
};

const SecondaryTherapyDialog: React.FC<SecondaryTherapyDialogProps> = ({secondaryTherapy, onClose, onSuccess}) => {
  const [form] = Form.useForm()
  const [currentStep, setCurrentStep] = useState<number>(0)
  const [drugOptions, setDrugOptions] = useState<Option[]>(
    secondaryTherapy?.drug ? [{value: secondaryTherapy.drug.drugId, label: secondaryTherapy.drug.name}] : []
  )
  const [statusDrugs, setStatusDrugs] = useState<any>(undefined)
  const [statusSubmit, setStatusSubmit] = useState<any>(undefined)
  const [submitErrorStatusCode, setSubmitErrorStatusCode] = useState<number | undefined>(undefined)

  const isEdit = Boolean(secondaryTherapy)

  // Not tied to a specific patient's patientDetailConfiguration (this dialog lives on the
  // standalone /therapies page), so it reads therapyConfigurations.secondaryTherapiesConfiguration
  // instead, falling back to the same default catalog used everywhere else in this app.
  const configuredDataSchema: SecondaryTherapyDataSchemaElement[] | undefined = useAppSelector(
    (state) => state.pspConfigReducer.data.therapyConfigurations?.secondaryTherapiesConfiguration?.dataSchema
  )
  const dataSchema: SecondaryTherapyDataSchemaElement[] = configuredDataSchema ?? SECONDARY_THERAPY_DEFAULT_DATA_SCHEMA
  const fieldLabel = (key: string, fallback: string): string => dataSchema.find((element) => element.value === key)?.name ?? fallback

  const searchDrugs = async (search: string) => {
    const drugs = await apiClient.getDrugs(search, setStatusDrugs) ?? []
    // Con ricerca vuota mostriamo l'intera lista (farmaci di scope + IA): ordinarla
    // alfabeticamente la rende più facile da scorrere.
    const sortedDrugs = search
      ? drugs
      : [...drugs].sort((a: Record<string, any>, b: Record<string, any>) => a.name.localeCompare(b.name, 'it'))
    setDrugOptions(sortedDrugs.map((drug: Record<string, any>) => ({value: drug._id, label: drug.name})))
  }

  useEffect(() => {
    searchDrugs('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const initialValues = {
    drugId: secondaryTherapy?.drug?.drugId,
    frequency: {
      ...secondaryTherapy?.frequency,
      each: buildDosageInitialValue(DEFAULT_FREQUENCY_UNIT_TYPE, secondaryTherapy?.frequency?.each),
      toleranceDays: buildDosageInitialValue(DEFAULT_TOLERANCE_DAYS_UNIT_TYPE, secondaryTherapy?.frequency?.toleranceDays),
      notificationTime: secondaryTherapy?.frequency?.notificationTime ? moment(secondaryTherapy.frequency.notificationTime, 'HH:mm') : undefined
    },
    assumptionDosage: buildDosageInitialValue(DEFAULT_DOSAGE_UNIT_TYPE, secondaryTherapy?.assumptionDosage),
    infusionRate: buildDosageInitialValue(DEFAULT_INFUSION_RATE_UNIT_TYPE, secondaryTherapy?.assumptionDosage?.infusionRate),
    secondaryDosage: buildDosageInitialValue(DEFAULT_DOSAGE_UNIT_TYPE, secondaryTherapy?.assumptionDosage?.secondaryDosage),
    dilution: secondaryTherapy?.assumptionDosage?.dilution,
    drugDosage: buildDosageInitialValue(DEFAULT_DOSAGE_UNIT_TYPE, secondaryTherapy?.drugDosage),
    dailyDosage: buildDosageInitialValue(DEFAULT_DOSAGE_UNIT_TYPE, secondaryTherapy?.dailyDosage),
    weeklyDosage: buildDosageInitialValue(DEFAULT_DOSAGE_UNIT_TYPE, secondaryTherapy?.weeklyDosage),
    monthlyDosage: buildDosageInitialValue(DEFAULT_DOSAGE_UNIT_TYPE, secondaryTherapy?.monthlyDosage),
    vialDosage: buildDosageInitialValue(DEFAULT_DOSAGE_UNIT_TYPE, secondaryTherapy?.vialDosage),
    calculationCriterion: buildDosageInitialValue(DEFAULT_CALCULATION_CRITERION_UNIT_TYPE, secondaryTherapy?.calculationCriterion),
    customDosages: buildCustomDosagesInitialValue(secondaryTherapy?.customDosages),
    modeOfAssumption: secondaryTherapy?.modeOfAssumption,
    deviceOfAssumption: secondaryTherapy?.deviceOfAssumption,
    speedOfDevice: buildDosageInitialValue(DEFAULT_SPEED_UNIT_TYPE, secondaryTherapy?.speedOfDevice),
    notes: secondaryTherapy?.notes
  }

  const getStepFields = (stepIndex: number): any[] => {
    if (stepIndex === 0) { return ['drugId'] }
    if (stepIndex === 1) {
      const asNeeded = form.getFieldValue(['frequency', 'asNeeded'])
      const isTimesConfigured = dataSchema.some((element) => element.value === 'frequency.times')
      return (asNeeded || !isTimesConfigured) ? [] : [['frequency', 'times'], ['frequency', 'each']]
    }
    return []
  }

  const goNext = async () => {
    await form.validateFields(getStepFields(currentStep))
    setCurrentStep((step) => step + 1)
  }

  const goBack = () => {
    setCurrentStep((step) => step - 1)
  }

  const formatTime = (value: any) => value ? moment(value).format('HH:mm') : undefined
  const buildPair = (value: any, unitType: any) => (value !== undefined && value !== null && unitType) ? {value, unitType} : undefined
  const cleanDosage = (dosage: any) => (dosage?.value !== undefined && dosage?.value !== null) ? dosage : undefined

  const onFinish = async () => {
    await form.validateFields(getStepFields(currentStep))
    const values = form.getFieldsValue(true)
    const {
      drugId, infusionRate, secondaryDosage, dilution, assumptionDosage,
      drugDosage, dailyDosage, weeklyDosage, monthlyDosage, vialDosage,
      calculationCriterion, speedOfDevice, customDosages, frequency,
      ...rest
    } = values

    const cleanAssumptionDosage = cleanDosage(assumptionDosage)

    const body: Record<string, any> = {
      ...rest,
      drug: {drugId},
      assumptionDosage: cleanAssumptionDosage ? {
        ...cleanAssumptionDosage,
        infusionRate: buildPair(infusionRate?.value, infusionRate?.unitType),
        secondaryDosage: buildPair(secondaryDosage?.value, secondaryDosage?.unitType),
        dilution
      } : undefined,
      drugDosage: cleanDosage(drugDosage),
      dailyDosage: cleanDosage(dailyDosage),
      weeklyDosage: cleanDosage(weeklyDosage),
      monthlyDosage: cleanDosage(monthlyDosage),
      vialDosage: cleanDosage(vialDosage),
      calculationCriterion: buildPair(calculationCriterion?.value, calculationCriterion?.unitType),
      speedOfDevice: buildPair(speedOfDevice?.value, speedOfDevice?.unitType),
      customDosages: customDosages?.length ? customDosages.map((customDosage: any) => ({
        value: customDosage.dosage?.value,
        unitType: customDosage.dosage?.unitType,
        infusionRate: buildPair(customDosage.infusionRate?.value, customDosage.infusionRate?.unitType),
        secondaryDosage: buildPair(customDosage.secondaryDosage?.value, customDosage.secondaryDosage?.unitType),
        dilution: customDosage.dilution,
        administrationHour: formatTime(customDosage.administrationHour)
      })) : undefined,
      frequency: frequency ? {
        ...frequency,
        each: buildPair(frequency.each?.value, frequency.each?.unitType),
        toleranceDays: buildPair(frequency.toleranceDays?.value, frequency.toleranceDays?.unitType),
        notificationTime: formatTime(frequency.notificationTime)
      } : undefined
    }

    if (isEdit) {
      // `statusSubmit` read here would still be the value of the previous render: onError is what
      // tells this call apart. A 403 means the therapy was entered by the centre (therapy-service
      // specs/2026-10-secondary-therapy-authz.md) - the dialog stays open with its message.
      let failed = false
      await apiClient.patchSecondaryTherapy(secondaryTherapy?._id, body, setStatusSubmit, (_message, statusCode) => {
        failed = true
        setSubmitErrorStatusCode(statusCode)
      })
      if (failed) { return }
    } else {
      await apiClient.postSecondaryTherapy(body, setStatusSubmit)
    }

    if (statusSubmit !== STATUS.REJECTED) {
      onSuccess()
    }
  }

  const getContainer = () => document.body

  const searchStepContent = (
    <Form.Item
      label='Farmaco'
      name='drugId'
      rules={[{required: true, message: 'Campo obbligatorio'}]}
    >
      <Select
        dropdownStyle={{zIndex: 1400}}
        filterOption={false}
        getPopupContainer={getContainer}
        loading={statusDrugs === STATUS.PENDING}
        onDropdownVisibleChange={(open) => { if (open) { searchDrugs('') } }}
        onSearch={searchDrugs}
        options={drugOptions}
        placeholder='Cerca un farmaco'
        showSearch
      />
    </Form.Item>
  )

  const notesStepContent = (
    <Form.Item label={fieldLabel('notes', 'Note')} name='notes'>
      <TextArea rows={4} />
    </Form.Item>
  )

  // "Frequenza" e "Dosaggio" restano sempre presenti: contengono campi strutturali/sempre visibili
  // (periodo terapia, "Al bisogno", "Numero assunzioni", "Ogni", "Personalizza dosaggi") che non
  // dipendono dalla configurazione. "Modalità di assunzione" e "Note" invece possono restare senza
  // alcun campo visibile, quindi vengono tolti dal wizard in quel caso.
  const steps = [
    {title: 'Cerca farmaco', content: searchStepContent},
    {title: 'Frequenza', content: <FrequencyFields dataSchema={dataSchema} form={form} />},
    {title: 'Dosaggio', content: <DosageFields dataSchema={dataSchema} form={form} />},
    ...(dataSchema.some((element) => element.group === 'administration')
      ? [{title: 'Modalità di assunzione', content: <ModeOfAssumptionFields dataSchema={dataSchema} />}]
      : []),
    ...(dataSchema.some((element) => element.value === 'notes')
      ? [{title: fieldLabel('notes', 'Note'), content: notesStepContent}]
      : []),
  ]

  const isLastStep = currentStep === steps.length - 1

  const body = (
    <div>
      <StepperGreyContainer>
        <Stepper currentStep={currentStep} steps={steps.map(({title}, index) => ({key: String(index), title}))} />
      </StepperGreyContainer>
      <div style={{maxHeight: '55vh', overflowY: 'auto', padding: '24px 0'}}>
        <BoldLabelsFormWrapper>
          <Form
            form={form}
            id='secondary-therapy-form'
            initialValues={initialValues}
            layout='vertical'
            onFinish={onFinish}
          >
            {steps.map((step, index) => (
              <div key={step.title} style={{display: currentStep === index ? 'block' : 'none'}}>
                {step.content}
              </div>
            ))}
            {statusSubmit === STATUS.REJECTED && (
              <Typography color='error' variant='body2'>
                {secondaryTherapyErrorMessage(submitErrorStatusCode, 'Errore nel salvataggio del farmaco.')}
              </Typography>
            )}
          </Form>
        </BoldLabelsFormWrapper>
      </div>
    </div>
  )

  const footer = (
    <div style={{display: 'flex', width: '100%', justifyContent: 'space-between'}}>
      <SecondaryButton onClick={currentStep === 0 ? onClose : goBack}>
        {currentStep === 0 ? 'Annulla' : 'Indietro'}
      </SecondaryButton>
      {isLastStep ? (
        <PrimaryButton form='secondary-therapy-form' key='submit' type='submit'>
          {'Salva'}
        </PrimaryButton>
      ) : (
        <PrimaryButton onClick={goNext}>
          {'Avanti'}
        </PrimaryButton>
      )}
    </div>
  )

  return (
    <DialogCustom
      body={body}
      footer={footer}
      maxWidth='md'
      onClose={onClose}
      title={{text: isEdit ? 'editAccessoryTherapy' : 'addAccessoryTherapy', format: true}}
    />
  )
}

export default SecondaryTherapyDialog
